import React from 'react';
import { BulkZipJobView } from '../../../types/bulk-zip';
import { ContentsDisclosure } from './contents-disclosure.component';
import styles from './downloads-center.component.module.scss';

interface Props {
    job: BulkZipJobView;
    onDownload: (jobId: string) => void;
    onDelete: (jobId: string) => void;
    onRetry: (jobId: string) => void;
}

interface State {
    showContents: boolean;
}

export class DownloadsCenterRow extends React.Component<Props, State> {
    state: State = { showContents: false };

    private toggle = () => this.setState((s) => ({ showContents: !s.showContents }));

    render() {
        const j = this.props.job;
        const isBuilding = j.processStage === 'BUILDING';
        const isReady = j.processStage === 'COMPLETED' && !j.failed && j.active;
        const isFailed = j.failed;
        // A job starts QUEUED, which matched none of the three states above, so a freshly
        // submitted job rendered as a bare filename: no status, no progress, no action. Nobody
        // saw it while the panel was only reachable from the header pill; opening the panel on
        // submit put that hole in front of every user, who then has to guess that waiting is
        // the right thing to do.
        const isWaiting = !isBuilding && !isReady && !isFailed;
        const pct =
            j.totalBytes > 0
                ? Math.min(100, Math.floor((j.bytesFlushed / j.totalBytes) * 100))
                : 0;

        return (
            <div className={styles['row']} data-status={j.processStage}>
                <div className={styles['title']}>
                    {isWaiting && <span>⏳ PREPARING </span>}
                    {isBuilding && <span>● BUILDING </span>}
                    {isReady && <span>✓ READY </span>}
                    {isFailed && <span>✕ FAILED </span>}
                    <code>{j.label}.zip</code>
                </div>

                {isWaiting && (
                    <div className={styles['caption']} role="status">
                        Getting your files together. Download appears here when it&apos;s ready —
                        you can keep using the site.
                    </div>
                )}

                {isBuilding && (
                    <div className={styles['progress']}>
                        <div className={styles['bar']}>
                            <span style={{ width: `${pct}%` }} />
                        </div>
                        <div className={styles['caption']}>
                            {pct}% — {j.currentFile ?? ''} ({j.filesCompleted}/{j.totalFiles})
                        </div>
                    </div>
                )}

                {isReady && (
                    <div className={styles['actions']}>
                        <button onClick={() => this.props.onDownload(j.jobId)}>Download</button>
                        <button
                            className={styles['icon']}
                            aria-label="Delete"
                            onClick={() => this.props.onDelete(j.jobId)}
                        >
                            🗑
                        </button>
                    </div>
                )}

                {isFailed && (
                    <div className={styles['actions']}>
                        <button onClick={() => this.props.onRetry(j.jobId)}>Retry</button>
                        <button
                            className={styles['icon']}
                            aria-label="Delete"
                            onClick={() => this.props.onDelete(j.jobId)}
                        >
                            🗑
                        </button>
                    </div>
                )}

                {j.excludedFiles.length > 0 && (
                    <div className={styles['excluded']}>
                        Omitted from this request:
                        <ul>
                            {j.excludedFiles.map((e) => (
                                <li key={e.fileId}>
                                    {e.fileName} — in{' '}
                                    <a href={`#bz-${e.sourceJobId}`}>{e.sourceJobLabel}</a>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <button className={styles['disclosure']} onClick={this.toggle}>
                    {this.state.showContents ? '▾' : '▸'} Contents ({j.fileNames.length} files)
                </button>
                {this.state.showContents && <ContentsDisclosure fileNames={j.fileNames} />}
            </div>
        );
    }
}
